/*
  "Add to your gift" — the cross-sell rows under the cart lines.

  The rows themselves are rendered by Liquid, from the same custom.cross_sell
  metafield the product page reads for "People also buy". Nothing is fetched
  here: there is one renderer for the row, and the block is complete the moment
  the drawer opens.

  So this only handles the button. Adding goes through window.tyohAddToBag, the
  same call the collection cards and the saved list use, so there is one cart
  pipeline on the site.
*/
class GiftAddons extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;

    // Delegated: the whole element is replaced when the cart re-renders, and a
    // listener per button would have to be rebound every time.
    this.addEventListener('click', (event) => {
      const arrow = event.target.closest('button[data-scroll]');
      if (arrow) {
        this.nudge(Number(arrow.dataset.scroll));
        return;
      }

      const button = event.target.closest('button[data-variant-id]');
      if (button) this.add(button);
    });

    this.setUpRail();
  }

  /* ------------------------------------------------------------- the rail */

  setUpRail() {
    this.rail = this.querySelector('.tyo-addons__list');
    this.nav = this.querySelector('.tyo-addons__nav');
    if (!this.rail || !this.nav) return;

    this.arrows = [...this.nav.querySelectorAll('button[data-scroll]')];

    const sync = () => this.syncArrows();
    this.rail.addEventListener('scroll', sync, { passive: true });

    /*
      The rail's width is not settled at connect time — the drawer may still be
      sliding in, and its own width is what decides whether anything overflows.
      A ResizeObserver answers whenever that becomes true rather than guessing
      at a delay.
    */
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(sync);
      this.observer.observe(this.rail);
    }

    sync();
  }

  syncArrows() {
    const overflowing = this.rail.scrollWidth - this.rail.clientWidth > 1;

    // Nothing to scroll: no controls. An arrow that does nothing is worse than
    // no arrow, because it is the shopper who is left wondering.
    this.nav.hidden = !overflowing;
    if (!overflowing) return;

    const atStart = this.rail.scrollLeft <= 1;
    const atEnd = this.rail.scrollLeft >= this.rail.scrollWidth - this.rail.clientWidth - 1;

    this.arrows.forEach((arrow) => {
      const back = Number(arrow.dataset.scroll) < 0;
      arrow.disabled = back ? atStart : atEnd;
    });
  }

  /*
    One card per press, measured off a real card rather than a constant — the
    width is a theme setting and the gap is a custom property, and a hard-coded
    number here would drift the moment either changed.
  */
  nudge(direction) {
    const card = this.rail?.querySelector('.tyo-addons__row');
    if (!card) return;

    const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    const step = card.getBoundingClientRect().width + gap;

    this.rail.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  async add(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;
    button.setAttribute('aria-disabled', 'true');

    try {
      await window.tyohAddToBag({ variantId: button.dataset.variantId, quantity: 1, opener: button });
      /*
        Nothing to re-render here. tyohAddToBag replaces .drawer__inner from the
        server, which rebuilds this element against the new bag — including this
        product now being excluded from its own suggestions.
      */
    } catch (error) {
      console.error('[gift-addons] could not add that to the bag', error);
      button.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('gift-addons')) customElements.define('gift-addons', GiftAddons);
